import { Button, TableSelectionEvent } from "@mcleod/components";
import { AutogenLayoutCollectionsToolPanel } from "./autogen/AutogenLayoutCollectionsToolPanel";
import { CustomerStatementsReport } from "./reports/CustomerStatementsReport";
import { InvoicesDueReminderReport } from "./reports/InvoicesDueReminderReport";
import { CollectionsReport } from "./reports/CollectionsReport";
import { ARCallHistory } from "./ARCallHistory";
import { CollectionsTableLayout } from "./Collections";
import { CommonDialogs } from "@mcleod/common";
import { ModelRow } from "@mcleod/core";
import { MultiReprintFreightBill } from "./MultiReprintFreightBill";
import { MultiReprintMiscBill } from "./MultiReprintMiscBill";

export class CollectionsToolPanel extends AutogenLayoutCollectionsToolPanel {
    private collectionsTableLayout: CollectionsTableLayout;

    override onLoad(): void {
        this.addMountListener(() => {
            this.initialize(this.owner);
        });
    }

    private getAllVisibleButtons(): Button[] {
        return this.panelButtons?.findComponentsByType(Button)?.filter(button => button.visible);
    }

    initialize(layout: CollectionsTableLayout) {
        this.collectionsTableLayout = layout;
        const visibleButtons = this.getAllVisibleButtons();
        if (visibleButtons?.length > 0) {
            this.collectionsTableLayout?.tableCollections?.addSelectionListener((event: TableSelectionEvent) => {
                this.tableOnSelect(event);
            });
        } else {
            this.removeAll();
        }
    }

    tableOnSelect(event: TableSelectionEvent) {
        this.syncToolPanel(event);
    }

    public syncToolPanel(event: TableSelectionEvent) {
        this.buttonReprintFreightBill.enabled = event.newRows?.length > 0;
        this.buttonReprintBalanceDue.enabled = event.newRows?.length > 0;
    }

    buttonCallHistoryOnClick() {
        const selectedRows = this.collectionsTableLayout?.tableCollections?.selectedRows ?? [];
        const customerId = selectedRows.length === 1 ?
            selectedRows[0].data.getString("customer_id", null) :
            this.collectionsTableLayout?.selectedCustomerRow?.getString("customer_id", null) ?? null;
        const customerName = selectedRows.length === 1 ?
            selectedRows[0].data.getString("customer_name", null) :
            this.collectionsTableLayout?.selectedCustomerRow?.getString("customer_name", null) ?? null;
        const orderId = selectedRows.length === 1 ? selectedRows[0].data.getString("order_id", null) : null;

        ARCallHistory.showSlideout({
            showSearchPanel: true,
            customerId,
            customerName,
            orderId
        });
    }

    private getSelectedCustomerIds(): string[] {
        const selectedRows = this.collectionsTableLayout?.tableCollections?.selectedRows || [];
        return selectedRows.map(row => row.data.get("customer_id")).filter(id => id != null);
    }

    buttonCustStmtRptOnClick() {
        const selectedCustomerIds = this.getSelectedCustomerIds();
        CustomerStatementsReport.showCustomerStatementsReportWithDecorator(selectedCustomerIds);
    }

    buttonCollectionsRptOnClick() {
        const selectedCustomerIds = this.getSelectedCustomerIds();
        CollectionsReport.showCollectionsReportWithDecorator(selectedCustomerIds);
    }

    buttonInvoicesDueRptOnClick() {
        const selectedCustomerIds = this.getSelectedCustomerIds();
        InvoicesDueReminderReport.showInvoicesDueReminderReportWithDecorator(selectedCustomerIds);
    }

    async buttonReprintFreightBillOnClick() {
        await this.openReprintSlideout(false);
    }

    async buttonReprintBalanceDueOnClick() {
        await this.openReprintSlideout(true);
    }

    async openReprintSlideout(isBalanceDue: boolean) {
        const selectedRows = this.collectionsTableLayout.tableCollections.selectedRows.map(row => row.data) as ModelRow[];

        // Separate rows by source type
        const miscBillRows = selectedRows.filter(row => row.getString("source", "") === "A");
        const freightBillRows = selectedRows.filter(row => row.getString("source", "") !== "A");
        const hasMiscBill = miscBillRows.length > 0;
        const hasFreightBill = freightBillRows.length > 0;

        // Mixed selection - prompt to print them separately
        if (hasFreightBill && hasMiscBill) {
            const response = await CommonDialogs.showYesNo(
                "You have selected both freight bills and miscellaneous bills. They will be printed in separate reports. Continue?"
            );
            if (response !== true) {
                return;
            }
        }

        const sendToImaging = await CommonDialogs.showYesNo("Send report to Imaging?");
        if (sendToImaging == null) {
            return;
        }

        // Print freight bills first
        if (hasFreightBill) {
            const includeOnlyInvoices = isBalanceDue && await CommonDialogs.showYesNo("Include only invoice(s) with balance for freight bills?");
            if (includeOnlyInvoices == null) {
                // User cancelled freight bill dialog; skip freight bills, continue to misc bills
            } else {
                const reprintFreightBillProps = {
                    openItems: freightBillRows,
                    includeOnlyInvoices: includeOnlyInvoices,
                    invoiceNumberFieldName: "latest_invoice_no",
                    isBalanceDue: isBalanceDue,
                    sendToImaging: sendToImaging,
                };
                MultiReprintFreightBill.openMultiReprintSlideout(reprintFreightBillProps);
            }
        }

        // Then print misc bills
        if (hasMiscBill) {
            const reprintMiscBillProps = {
                openItems: miscBillRows,
                invoiceNumberFieldName: "latest_invoice_no",
                isBalanceDue: isBalanceDue,
                sendToImaging: sendToImaging,
            };
            MultiReprintMiscBill.openMultiReprintSlideout(reprintMiscBillProps);
        }
    }
}
